import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Alert, AlertDescription } from 'src/components/ui/alert';
import { Eye, EyeOff, ShieldCheck, Lock } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { UserService } from 'src/services/api/UserService';
import { trackEvent } from 'src/utils/analytics';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validatePassword = (value) => {
    const hasMinLength = value.length >= 8;
    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);
    const hasNoSpaces = !/\s/.test(value);

    return (
      hasMinLength &&
      hasLowerCase &&
      hasUpperCase &&
      hasNumber &&
      hasSpecialChar &&
      hasNoSpaces
    );
  };

  const passwordRules = useMemo(
    () => ({
      minLength: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[@$!%*?&]/.test(password),
      noSpaces: !/\s/.test(password),
    }),
    [password]
  );

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setPassword(value);

    setPasswordError('');
    setConfirmPasswordError('');

    if (!value.trim()) {
      setPasswordError('Password is required');
      return;
    }

    if (!validatePassword(value)) {
      setPasswordError(
        'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character'
      );
    }

    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;
    setConfirmPassword(value);

    setConfirmPasswordError('');

    if (!value.trim()) {
      setConfirmPasswordError('Please confirm your password');
      return;
    }

    if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setPasswordError('');
    setConfirmPasswordError('');
    setError('');
    trackEvent('form_submit', 'Reset Password', 'reset');

    if (!password.trim() || !confirmPassword.trim()) {
      if (!password.trim()) setPasswordError('Password is required');
      if (!confirmPassword.trim())
        setConfirmPasswordError('Please confirm your password');
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character'
      );
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);

      const response = await UserService.resetPassword({
        token: resetToken,
        new_password: password,
      });
      // Check if response has success property, or if it's a direct data response
      if (response.success === false) {
        trackEvent('form_error', 'Reset Password', 'reset');
        setError(
          response.message || 'Failed to reset password. Please try again.'
        );
        return;
      }

      // If response doesn't have success property but we got a response, consider it successful
      if (!response.success && !response.message) {
        trackEvent('form_error', 'Reset Password', 'reset');
        setError('Failed to reset password. Please try again.');
        return;
      }

      trackEvent('form_success', 'Reset Password', 'reset');
      toast({ message: 'Password updated successfully', variant: 'success' });
      navigate('/join-us', {
        state: {
          message: 'Password reset! Login with your new password.',
        },
      });
    } catch (err) {
      console.error('Reset password error:', err);
      trackEvent('form_error', 'Reset Password', 'reset');
      setError(err.message || 'Failed to reset password. Please try again.');
      toast({
        message: err.message || 'Failed to reset password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If no token is present, show error
  // if (!resetToken) {
  //   return (
  //     <div className="h-screen bg-gray-100 flex overflow-hidden">
  //       invalid reset link
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-[100%] overflow-hidden bg-gradient-to-b from-blue-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex flex-1 overflow-hidden justify-center">
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-blue-100 p-6 max-w-md mt-4 h-fit w-full">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-base font-semibold">Reset password</h1>
            <p className="text-xs text-muted-foreground">
              For your email sign‑in
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 w-full mt-4">
          <div className="space-y-2">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={password}
                onChange={handlePasswordChange}
                onFocus={() => setShowPasswordTooltip(true)}
                onBlur={() => setShowPasswordTooltip(false)}
                className={`h-9 ${passwordError ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            {(showPasswordTooltip || password) && (
              <div className="flex flex-wrap gap-2 pt-1">
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.minLength
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  8+ chars
                </div>
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.upper
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  A-Z
                </div>
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.lower
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  a-z
                </div>
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.number
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  0-9
                </div>
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.symbol
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  symbol
                </div>
                <div
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    passwordRules.noSpaces
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  no spaces
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className={`h-9 ${
                  confirmPasswordError ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {confirmPasswordError && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {confirmPasswordError}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full h-9" disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Resetting Password...
              </div>
            ) : (
              'Reset Password'
            )}
          </Button>

          <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>
              Your password is encrypted and never stored in plain text.
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
